// Copyright 2020 Sorama B.V.

import { autoinject, bindable, computedFrom, containerless, customElement } from "aurelia-framework";
import { TimelineEntryDTO } from "datatypes/SoramaWebApi";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { WorkflowModuleTimelineEvent } from "libraries/WorkflowFramework/WorkflowModule/WorkflowModuleTimeEvent";
import { ProjectRepository } from "repositories/ProjectRepository";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ExportService } from "services/Export/ExportService";
import { TimelineService } from "services/TimeLine/TimelineService";

/**
 * MODULE OPERATION DESCRIPTION - TimelineModule
 * The timeline module gets the list of timeline entries from Project repository and
 * renders it to the front end.
 */
@customElement("timeline")
@containerless // We expect the parent to be a flexbox in order to get the infinite scroll working properly
@autoinject
export class TimelineModule {
    @bindable
    public projectId: string;

    @bindable
    public timelineActionName: string;

    @bindable
    public disableHighlight = false;

    @bindable
    public timelineAction: (entry: TimelineEntryDTO) => void;

    @computedFrom("timelineActionName")
    protected get actualTimelineActionName() {
        return this.timelineActionName ? this.timelineActionName : "Open";
    }

    protected pagedRequest: IPagedRequest<TimelineEntryDTO>;

    private get measurementNameMap() {
        return this.timelineService.measurementNameMap;
    }

    private getMeasurementId(entry: TimelineEntryDTO): string {
        return this.timelineService.getMeasurementId(entry);
    }

    private ensureHasMeasurementName(entry: TimelineEntryDTO): boolean {
        return this.timelineService.ensureHasMeasurementName(entry);
    }

    constructor(
        private readonly projectRepository: ProjectRepository,
        private readonly timelineEventRepository: TimelineEntriesRepository,
        private readonly exportService: ExportService,
        private readonly timelineService: TimelineService
    ) {}

    public bind(): void {
        this.projectIdChanged(this.projectId);
    }

    //#region Property Changed Methods
    public projectIdChanged(newId: string): void {
        // Create a new paged request with filtering
        this.pagedRequest = this.projectRepository.getTimelineEntries(newId);
    }

    //#region Property Changed Callbacks

    protected open(entry: TimelineEntryDTO, event: MouseEvent): void {
        this.timelineService.open(entry);

        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    protected openOrAction(entry: TimelineEntryDTO, event: MouseEvent) {
        if (this.timelineAction) {
            this.timelineAction(entry);

            event.stopPropagation();
            event.stopImmediatePropagation();
        } else {
            this.open(entry, event);
        }
    }

    protected toggleHighlight(entry: TimelineEntryDTO, highlightStatus: boolean, event: MouseEvent): Promise<void> {
        //Bad workaround to be able to get the CanvasAddModule to add the selected timeline entry to the presentation
        if (this.disableHighlight) {
            this.openOrAction(entry, event);
        } else {
            if (!highlightStatus && highlightStatus != entry.highlighted) {
                const timelineHighlightEvent = new WorkflowModuleTimelineEvent(
                    entry,
                    this.timelineEventRepository,
                    this.exportService
                );
                return timelineHighlightEvent.setHighlightEvent(highlightStatus);
            }
        }
    }

    //#endregion
}
